import { format } from "date-fns";
import type { Matcher } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { Calendar } from "@/components/ui/calendar";
import { toIsoDate } from "@/lib/therapist-scheduling/date";
import { cn } from "@/lib/utils";

export type ScheduleCalendarModifiers = {
	/** Continuity therapist available on this date (AVAILABLE / PARTIALLY_BOOKED). */
	available?: (date: Date) => boolean;
	/** Continuity therapist NOT available (NOT_WORKING / FULLY_BOOKED / no-entry). */
	unavailable?: (date: Date) => boolean;
};

type ScheduleCalendarProps = {
	selected?: Date;
	onSelect: (date: Date | undefined) => void;
	/** Optional min selectable date. Omit for no lower bound. */
	minDate?: Date;
	/** Optional max selectable date. Omit for no upper bound. */
	maxDate?: Date;
	/**
	 * Optional continuity coloring (Visit 2++). Unavailable dates are colored but
	 * stay CLICKABLE — selecting one triggers the show-all fallback (§4.2).
	 */
	modifiers?: ScheduleCalendarModifiers;
	/**
	 * Show the continuity rows (available / substitute) in the legend. Backdate is
	 * always shown. Pass true only when continuity coloring is active.
	 */
	showContinuityLegend?: boolean;
	/**
	 * "YYYY-MM-DD" dates already taken by OTHER visits in this booking. They are
	 * struck through so the admin sees the date is used elsewhere (still clickable).
	 */
	scheduledDates?: string[];
	className?: string;
};

/**
 * Date-only picker for the schedule flow. When minDate/maxDate are given,
 * out-of-range dates are disabled; omit them for an unbounded picker.
 * Availability coloring never disables a date.
 */
export function ScheduleCalendar({
	selected,
	onSelect,
	minDate,
	maxDate,
	modifiers,
	showContinuityLegend = false,
	scheduledDates,
	className,
}: ScheduleCalendarProps) {
	const { locale, tzDate } = useDateContext();
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker.calendar_legend",
	});

	// "Today" in the configured timezone; anything strictly before is a backdate.
	const todayIso = format(new Date(), "yyyy-MM-dd", { in: tzDate });
	const isPast = (date: Date) => toIsoDate(date) < todayIso;

	const scheduledSet = new Set(scheduledDates ?? []);
	const hasScheduled = scheduledSet.size > 0;

	const dayModifiers: Record<string, Matcher> = {
		past: isPast,
	};
	// Continuity coloring never applies to backdates — those read as gray so the
	// green/orange stays reserved for actionable (present/future) dates.
	if (modifiers?.available) {
		const available = modifiers.available;
		dayModifiers.available = (date: Date) => !isPast(date) && available(date);
	}
	if (modifiers?.unavailable) {
		const unavailable = modifiers.unavailable;
		dayModifiers.unavailable = (date: Date) =>
			!isPast(date) && unavailable(date);
	}
	if (hasScheduled) {
		dayModifiers.scheduled = (date: Date) => scheduledSet.has(toIsoDate(date));
	}

	return (
		<div className={cn("flex flex-col", className)}>
			<Calendar
				mode="single"
				locale={locale}
				selected={selected}
				onSelect={onSelect}
				defaultMonth={selected ?? minDate}
				fromDate={minDate}
				toDate={maxDate}
				modifiers={dayModifiers}
				modifiersClassNames={{
					available:
						"bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
					unavailable:
						"bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
					// Backdate: plain gray, no strike (strike is reserved for dates taken
					// by other visits below).
					past: "text-muted-foreground opacity-50",
					// Taken by another visit: keep the underlying color but strike it —
					// line-through composes cleanly on top of available / unavailable.
					scheduled: "line-through",
				}}
			/>
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-border border-t px-3 py-2 text-muted-foreground text-xs">
				<span className="font-medium">{t("title")}:</span>
				{showContinuityLegend && (
					<>
						<span className="flex items-center gap-1.5">
							<span className="size-2.5 rounded-[3px] bg-emerald-500" />
							{t("available")}
						</span>
						<span className="flex items-center gap-1.5">
							<span className="size-2.5 rounded-[3px] bg-orange-500" />
							{t("substitute")}
						</span>
					</>
				)}
				<span className="flex items-center gap-1.5">
					<span className="size-2.5 rounded-[3px] bg-muted-foreground/40" />
					{t("backdate")}
				</span>
				{hasScheduled && (
					<span className="flex items-center gap-1.5">
						<span className="h-px w-3 bg-foreground/70" />
						<span className="line-through">{t("scheduled")}</span>
					</span>
				)}
			</div>
		</div>
	);
}
